/**
 * Os campos do formulário da paciente, no visual do desenho "Indique a Eva"
 * (A127) — classes em `formulario.css`. Nasceu como porte de
 * `components/forms/FormRenderer.tsx` do mobile (a data é texto com máscara
 * DD/MM/AAAA, o caminho web de lá); o mobile segue com o dele.
 *
 * O que mudou além do visual:
 *   - EMAIL e PHONE validam o formato, e o telefone usa a máscara do
 *     onboarding (A109);
 *   - BOOLEAN com `formato: 'CAIXA'` é uma caixa de marcar e responde
 *     `false` quando fica desmarcada;
 *   - TERMS mostra o texto do campo e o "Texto do aceite" como o admin os
 *     pré-visualiza. Antes saía "Eu aceito os {aceite}", que com o aceite
 *     "Li e aceito o Termo…" da POG virava "Eu aceito os Li e aceito…", e o
 *     texto do campo não aparecia.
 */
import { useEffect, useId, useState, type ReactNode } from 'react';
import { Loader } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { emailDeFormularioValido, formatarTelefone, telefoneBrasileiroValido } from '@eva/schemas';
import type { CareForm, FormField } from '@eva/domain';
import './formulario.css';

interface FormRendererProps {
  form: CareForm;
  onSubmit: (data: Record<string, unknown>) => void;
  isSubmitting?: boolean;
}

const texto = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const lista = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

function opcoes(field: FormField): Array<{ value: string | number | boolean; label: string }> {
  return (field.options || []).map((opt) => (typeof opt === 'string' ? { value: opt, label: opt } : { value: opt.value, label: opt.label }));
}

function estaVazio(value: unknown): boolean {
  return value === undefined || value === '' || value === null || (Array.isArray(value) && value.length === 0);
}

const ehCaixa = (field: FormField) => field.type === 'BOOLEAN' && field.formato === 'CAIXA';

/** Campos em que "obrigatório" quer dizer "marcado", não "respondido". */
const precisaMarcar = (field: FormField) => field.type === 'TERMS' || ehCaixa(field);

/** O erro de um campo com o valor atual, ou nada. */
function erroDoCampo(field: FormField, value: unknown): string | null {
  if (field.type === 'SECTION' || field.type === 'INFO') return null;
  if (field.required) {
    if (precisaMarcar(field) && value !== true) return field.type === 'TERMS' ? 'Você precisa aceitar os termos' : 'Marque para continuar';
    if (estaVazio(value)) return 'Este campo é obrigatório';
  }
  if (estaVazio(value)) return null;
  if (field.type === 'EMAIL' && !emailDeFormularioValido(texto(value))) return 'Informe um e-mail válido.';
  if (field.type === 'PHONE' && !telefoneBrasileiroValido(texto(value))) return 'Informe o telefone com DDD.';
  return null;
}

function Caixa({ marcada }: { marcada: boolean }) {
  return <span className="eva-form-caixa">{marcada && <IconCheck size={15} stroke={2.6} />}</span>;
}

export function FormRenderer({ form, onSubmit, isSubmitting }: FormRendererProps) {
  const prefixo = useId();
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    // Valores iniciais
    const inicial: Record<string, unknown> = {};
    form.fields?.forEach((field) => {
      if (field.defaultValue !== undefined) inicial[field.id] = field.defaultValue;
      if (field.type === 'MULTISELECT') inicial[field.id] = inicial[field.id] || [];
      // Desmarcada também é resposta: "não autorizo" fica registrado.
      if (ehCaixa(field) && inicial[field.id] === undefined) inicial[field.id] = false;
    });
    setFormData(inicial);
  }, [form]);

  // O botão acende quando os obrigatórios estão preenchidos; o formato
  // (e-mail, telefone) é conferido ao sair do campo e ao enviar.
  useEffect(() => {
    let valido = true;
    form.fields?.forEach((field) => {
      if (!field.required) return;
      const value = formData[field.id];
      if (precisaMarcar(field) ? value !== true : estaVazio(value)) valido = false;
    });
    setIsValid(valido);
  }, [formData, form]);

  const limparErro = (fieldId: string) => {
    if (!errors[fieldId]) return;
    setErrors((prev) => {
      const novos = { ...prev };
      delete novos[fieldId];
      return novos;
    });
  };

  const mudar = (fieldId: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
    limparErro(fieldId);
  };

  /** Ao sair do campo: só o formato, para não acusar "obrigatório" em quem só passou por ele. */
  const conferirFormato = (field: FormField) => {
    const value = formData[field.id];
    if (estaVazio(value)) return;
    const erro = erroDoCampo(field, value);
    if (erro) setErrors((prev) => ({ ...prev, [field.id]: erro }));
  };

  const alternarMulti = (fieldId: string, value: unknown) => {
    const atual = lista(formData[fieldId]);
    mudar(fieldId, atual.includes(value) ? atual.filter((v) => v !== value) : [...atual, value]);
  };

  /*
   * Telefone com a máscara do onboarding (A109): só dígitos entram, e apagar
   * um caractere da máscara (hífen, parêntese) apaga o dígito antes dele —
   * sem isto a máscara voltava igual e o apagar parecia não funcionar.
   */
  const mudarTelefone = (fieldId: string, digitado: string) => {
    const anterior = texto(formData[fieldId]);
    let digitos = digitado.replace(/\D/g, '');
    if (digitado.length < anterior.length && digitos === anterior.replace(/\D/g, '')) digitos = digitos.slice(0, -1);
    mudar(fieldId, formatarTelefone(digitos));
  };

  const validar = () => {
    const novos: Record<string, string> = {};
    form.fields?.forEach((field) => {
      const erro = erroDoCampo(field, formData[field.id]);
      if (erro) novos[field.id] = erro;
    });
    setErrors(novos);
    return Object.keys(novos).length === 0;
  };

  const enviar = () => {
    if (validar()) onSubmit(formData);
  };

  const idDe = (field: FormField) => `${prefixo}-${field.id}`;

  const rotulo = (field: FormField, comoLegenda = false) => {
    const conteudo = (
      <>
        {field.label}
        {field.required ? ' *' : <span className="eva-form-opcional"> (opcional)</span>}
      </>
    );
    return comoLegenda ? (
      <span className="eva-form-rotulo" id={`${idDe(field)}-rotulo`}>
        {conteudo}
      </span>
    ) : (
      <label className="eva-form-rotulo" htmlFor={idDe(field)}>
        {conteudo}
      </label>
    );
  };

  const campo = (field: FormField, corpo: ReactNode) => {
    const error = errors[field.id];
    return (
      <div key={field.id}>
        {corpo}
        {error && (
          <div className="eva-form-erro" id={`${idDe(field)}-erro`} role="alert">
            {error}
          </div>
        )}
      </div>
    );
  };

  const propsDaEntrada = (field: FormField) => ({
    id: idDe(field),
    className: 'eva-form-entrada',
    'aria-invalid': errors[field.id] ? ('true' as const) : undefined,
    'aria-describedby': errors[field.id] ? `${idDe(field)}-erro` : undefined,
    onBlur: () => conferirFormato(field),
  });

  const renderizar = (field: FormField) => {
    switch (field.type) {
      case 'SECTION':
        return (
          <div key={field.id} className="eva-form-secao">
            {field.label}
          </div>
        );

      case 'INFO':
        return (
          <div key={field.id} className="eva-form-info">
            {field.label}
          </div>
        );

      case 'TEXT':
        return campo(
          field,
          <>
            {rotulo(field)}
            <input {...propsDaEntrada(field)} placeholder={field.placeholder} value={texto(formData[field.id])} onChange={(e) => mudar(field.id, e.currentTarget.value)} />
          </>,
        );

      case 'EMAIL':
        return campo(
          field,
          <>
            {rotulo(field)}
            <input
              {...propsDaEntrada(field)}
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder={field.placeholder || 'email@exemplo.com.br'}
              value={texto(formData[field.id])}
              onChange={(e) => mudar(field.id, e.currentTarget.value.trim())}
            />
          </>,
        );

      case 'PHONE':
        return campo(
          field,
          <>
            {rotulo(field)}
            <input
              {...propsDaEntrada(field)}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder={field.placeholder || '(00) 0 0000-0000'}
              value={texto(formData[field.id])}
              onChange={(e) => mudarTelefone(field.id, e.currentTarget.value)}
            />
          </>,
        );

      case 'NUMBER':
        return campo(
          field,
          <>
            {rotulo(field)}
            <div className="eva-form-unidade-linha">
              <input
                {...propsDaEntrada(field)}
                placeholder={field.placeholder}
                inputMode="decimal"
                value={texto(formData[field.id])}
                onChange={(e) => mudar(field.id, e.currentTarget.value.replace(/[^0-9.,]/g, ''))}
              />
              {field.unit && <span className="eva-form-unidade">{field.unit}</span>}
            </div>
          </>,
        );

      case 'DATE':
        return campo(
          field,
          <>
            {rotulo(field)}
            <input
              {...propsDaEntrada(field)}
              placeholder="DD/MM/AAAA"
              inputMode="numeric"
              maxLength={10}
              value={texto(formData[field.id])}
              onChange={(e) => {
                let limpo = e.currentTarget.value.replace(/\D/g, '');
                if (limpo.length > 8) limpo = limpo.substring(0, 8);
                let mascarado = limpo;
                if (limpo.length > 2) mascarado = `${limpo.substring(0, 2)}/${limpo.substring(2)}`;
                if (limpo.length > 4) mascarado = `${mascarado.substring(0, 5)}/${limpo.substring(4)}`;
                mudar(field.id, mascarado);
              }}
            />
          </>,
        );

      case 'BOOLEAN':
        if (ehCaixa(field)) {
          const marcada = formData[field.id] === true;
          return campo(
            field,
            <label className="eva-form-marcar">
              <input type="checkbox" checked={marcada} onChange={(e) => mudar(field.id, e.currentTarget.checked)} />
              <Caixa marcada={marcada} />
              <span>
                {field.label}
                {field.required && ' *'}
              </span>
            </label>,
          );
        }
        return campo(
          field,
          <>
            {rotulo(field, true)}
            <div className="eva-form-opcoes" data-lado-a-lado="true" role="group" aria-labelledby={`${idDe(field)}-rotulo`}>
              {[
                { valor: true, label: field.trueLabel || 'Sim' },
                { valor: false, label: field.falseLabel || 'Não' },
              ].map((op) => (
                <button key={String(op.valor)} type="button" className="eva-form-opcao" aria-pressed={formData[field.id] === op.valor} onClick={() => mudar(field.id, op.valor)}>
                  {op.label}
                </button>
              ))}
            </div>
          </>,
        );

      case 'SELECT':
        return campo(
          field,
          <>
            {rotulo(field, true)}
            <div className="eva-form-opcoes" role="radiogroup" aria-labelledby={`${idDe(field)}-rotulo`}>
              {opcoes(field).map((op) => (
                <button
                  key={String(op.value)}
                  type="button"
                  role="radio"
                  className="eva-form-opcao"
                  aria-checked={formData[field.id] === op.value}
                  onClick={() => mudar(field.id, op.value)}
                >
                  {op.label}
                </button>
              ))}
            </div>
          </>,
        );

      case 'MULTISELECT':
        return campo(
          field,
          <>
            {rotulo(field, true)}
            <div className="eva-form-opcoes" role="group" aria-labelledby={`${idDe(field)}-rotulo`}>
              {opcoes(field).map((op) => {
                const selecionado = lista(formData[field.id]).includes(op.value);
                return (
                  <button
                    key={String(op.value)}
                    type="button"
                    role="checkbox"
                    className="eva-form-opcao"
                    aria-checked={selecionado}
                    onClick={() => alternarMulti(field.id, op.value)}
                  >
                    <Caixa marcada={selecionado} />
                    {op.label}
                  </button>
                );
              })}
            </div>
          </>,
        );

      case 'TERMS': {
        const marcada = formData[field.id] === true;
        return campo(
          field,
          <>
            {field.label && <p className="eva-form-texto-do-termo">{field.label}</p>}
            <label className="eva-form-marcar">
              <input type="checkbox" checked={marcada} onChange={(e) => mudar(field.id, e.currentTarget.checked)} />
              <Caixa marcada={marcada} />
              <span>
                {field.termsLabel || 'Li e concordo com os termos.'}
                {field.required && ' *'}
                {field.termsFileUrl && (
                  <>
                    {' '}
                    <a className="eva-form-link" href={field.termsFileUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                      Ler o termo
                    </a>
                  </>
                )}
              </span>
            </label>
          </>,
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="eva-form-corpo">
      {form.fields && form.fields.map(renderizar)}

      <button type="button" className="eva-form-enviar" onClick={enviar} disabled={isSubmitting || !isValid}>
        {isSubmitting && <Loader size={16} color="#fff" />}
        {form.textoDoBotao || 'Enviar'}
      </button>
    </div>
  );
}
